import { z } from 'zod';
/**
 * Neutral server-host contract used by optional business modules to publish
 * versioned read-only facts without importing another optional module.
 * Consumers still validate the domain response against the provider's public
 * capability contract.
 */
export const platformOptionalFactProviderHostCapability = 'platform.optional-fact-provider-host.v1';
export const platformOptionalFactProviderSlot = 'platform.server.optional-facts';
/** Neutral bounded-context slot for read-only tenant-qualified context facts. */
export const platformOptionalContextProviderSlot = 'platform.server.optional-context';
const capabilitySchema = z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
export const platformOptionalFactProviderRequestSchema = z.strictObject({
    consumerModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/),
    requestedCapability: capabilitySchema,
    resourceType: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
    resourceId: z.string().uuid(),
    preferredSourceId: z.string().uuid().nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
});
export const platformOptionalFactUnavailableReasonSchema = z.enum([
    'not-installed',
    'permission-denied',
    'provider-invalid',
    'provider-failed',
    'source-not-found',
    'provider-unavailable',
    'unsupported-capability',
]);
export const platformOptionalCountFactSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        capability: capabilitySchema,
        count: z.number().int().positive().safe(),
        reasonCode: z.null(),
    }),
    z.strictObject({
        state: z.literal('missing'),
        capability: capabilitySchema,
        count: z.literal(0),
        reasonCode: z.null(),
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        capability: capabilitySchema,
        count: z.null(),
        reasonCode: platformOptionalFactUnavailableReasonSchema,
    }),
]);
export const platformOptionalFactBundleSchema = z.strictObject({
    contractVersion: z.literal(platformOptionalFactProviderHostCapability),
    providerModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/),
    consumerModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/),
    requestedCapability: capabilitySchema,
    resourceType: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
    resourceId: z.string().uuid(),
    projectId: z.string().uuid().nullable(),
    facts: z.array(platformOptionalCountFactSchema).min(1).max(100),
}).superRefine((bundle, context) => {
    const capabilities = bundle.facts.map(({ capability }) => capability);
    if (new Set(capabilities).size !== capabilities.length) {
        context.addIssue({
            code: 'custom',
            path: ['facts'],
            message: 'An optional fact bundle cannot repeat a source capability.',
        });
    }
});
export function createPlatformOptionalCountFact(capability, count) {
    const normalizedCount = z.number().int().nonnegative().safe().parse(count);
    return platformOptionalCountFactSchema.parse(normalizedCount > 0
        ? { state: 'present', capability, count: normalizedCount, reasonCode: null }
        : { state: 'missing', capability, count: 0, reasonCode: null });
}
export function createPlatformUnavailableCountFact(capability, reasonCode) {
    return platformOptionalCountFactSchema.parse({
        state: 'unavailable',
        capability,
        count: null,
        reasonCode,
    });
}
//# sourceMappingURL=platform-optional-fact-provider-host.contract.js.map